import { ColumnState } from 'ag-grid-community';
import { VirtualColumnDef } from 'main/hooks/useEventAttributes';
import { RoleHorizonAggregateMapping } from 'main/hooks/iam';
import { NodeMap } from 'main/hooks/useNodesData/nodeTypes';
import { ItemLayout } from 'main/pages/investigations/components/Dashboard';
import { TimelineSettings } from 'main/pages/investigations/journey/InvestigationsJourney';
import { QueryGraphItem } from 'main/pages/investigations/query/query-types';
import { PaletteRules } from 'main/pages/investigations/utils/paletteRules';
import { Layouts } from 'react-grid-layout';
import { store, useGlobalState, useGlobalStateReducer } from 'state-pool';
import DWNWorkflowManager from 'main/contexts/DWNWorkflowManager';
import { QueryDateRangeJSON } from '~/main/components/datetime/datetimeTypes';

export interface RealmData {
  [key: string]: {
    customerName: string;
    updateRequired: boolean;
    displayName?: string;
  };
}

export interface PermissionsData {
  permissions: string[];
  nodePermissions: RoleHorizonAggregateMapping[];
  forceReload: boolean;
}

export interface LogoutAction {
  type: 'IFRAME';
  url: string;
}

export interface LogoutActions {
  actions: LogoutAction[];
}

export interface NodeData {
  nodes: NodeMap;
  selected?: string;
  forceReload: boolean;
  initializing: boolean;
}

export interface NodeQueryStatus {
  nodeId?: string;
  isInit?: boolean;
}

export interface QueryData {
  loading: boolean;
  data: any[];
  error?: string;
}

export interface InvestigationTemplate {
  id: string;
  type: string;
  label: string;
  value?: string;
  isGlobal?: boolean;
  user: string | null;
  data?: TemplateConfig;
}

export const InvestigationTypes = ['Query', 'Journey', 'Identifier', 'Incident'] as const;

export const InvestigationType = {
  Query: 0,
  Journey: 1,
  Identifier: 2,
  Incident: 3,
} as const;

export type Investigation = (typeof InvestigationType)[keyof typeof InvestigationType];

export function isInvestigationType(x: unknown): x is Investigation {
  return Object.values(InvestigationType).find((y) => x === y) !== undefined;
}

export type TemplateConfig =
  | QueryTemplateConfig
  | JourneyTemplateConfig
  | IdentifierTemplateConfig
  | IncidentsTemplateConfig;

export interface TemplateData {
  templates: InvestigationTemplate[];
  currentTemplateConfig: TemplateConfig[];
  initialSelectedTemplateConfig: (InvestigationTemplate | undefined)[];
}

export interface WorkflowEditorData {
  manager: DWNWorkflowManager;
}

export interface QueryTemplateConfig {
  type: typeof InvestigationType.Query;
  query?: string;
  grid?: ColumnState[];
  layout?: Layouts;
  graphs?: ItemLayout<QueryGraphItem>[];
  reflex?: number[];
  palette?: PaletteRules;
  virtuals?: VirtualColumnDef[];
  queryDateRange?: QueryDateRangeJSON;
}

export interface JourneyTemplateConfig {
  type: typeof InvestigationType.Journey;
  grid?: ColumnState[];
  timeline?: TimelineSettings;
  reflex?: number[];
  palette?: PaletteRules;
  virtuals?: VirtualColumnDef[];
}

export interface IdentifierTemplateConfig {
  type: typeof InvestigationType.Identifier;
  search: unknown;
}

export function isIdentifierTemplateConfig(x: unknown): x is IdentifierTemplateConfig {
  return x !== null && typeof x === 'object' && 'type' in x && x.type === InvestigationType.Identifier && 'search' in x;
}

export interface IncidentsTemplateConfig {
  type: typeof InvestigationType.Incident;
  grid?: ColumnState[];
  reflex?: number[];
  palette?: PaletteRules;
  virtuals?: VirtualColumnDef[];
  graphs?: ItemLayout<QueryGraphItem>[];
}

export interface EventTemplateConfig {
  type: number;
  layout?: Layouts;
}

function updateStash(key: string, obj: any) {
  const localStash = getFullStash();
  localStash[key] = obj;
  window.localStorage.setItem('dwn-stash', JSON.stringify(localStash));
}

function getStash(key: string) {
  return getFullStash()[key];
}

function getFullStash() {
  const raw = window.localStorage.getItem('dwn-stash');
  const localStash = raw ? JSON.parse(raw) : {};
  return localStash;
}

function clearStash() {
  window.localStorage.removeItem('dwn-stash');
}

const DwnStash = {
  set: (key: string, obj: any) => updateStash(key, obj),
  get: (key: string) => getStash(key),
  clear: () => clearStash(),
};

export enum State {
  REALMS = 'realms',
  JUMP_KEYCLOAK = 'jumpKeycloak',
  PERMISSIONS = 'permissions',
  NODES = 'nodes',
  NODE_VIPER = 'node_viper',
  NODE_VIPER_STATUS = 'node_viper_status',
  NODE_INTROSPECTION = 'node_introspection',
  NODE_INTROSPECTION_STATUS = 'node_introspection_status',
  NODE_AUTO = 'node_auto',
  NODE_AUTO_STATUS = 'node_auto_status',
  QUERY = 'query',
  LOGOUT_ACTIONS = 'logoutActions',
  WORKFLOW_EDITOR = 'workflowEditor',
}

export const useAppState = <T>(
  state: State,
): {
  data: T;
  setData: (arg0: T) => void;
  updateData: (arg0: (subArg: T) => void) => void;
} => {
  let [data, setData, updateData] = useGlobalState(state);
  return {
    data,
    setData,
    updateData,
  };
};

export const useAppReducer = <T, U>(
  reducer: (currentState: T, action: U) => T,
  state: State,
): {
  data: T;
  dispatch: (action: U) => void;
} => {
  const [data, dispatch] = useGlobalStateReducer(reducer, state);
  return {
    data,
    dispatch,
  };
};

export const setAppState = <T>(state: State, init: T) => {
  store.setState(state, init);
};

export default DwnStash;
